import { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import api from '@helpers/api.js';

import './styles.scss';

import CustomModal from '@components/CustomModal/index.js';
import { Input, Button, Text } from '@fragments';

import {
    GavelRounded,
    AttachFileRounded,
    PictureAsPdfRounded,
    ImageRounded,
    InsertDriveFileRounded,
    CloseRounded,
    AddRounded,
    DescriptionRounded,
} from '@mui/icons-material';

const MAX_FILES = 10;
// Backend validates ~10MB per document; the total cap keeps the JSON body
// (base64 inflates ~33%) under the server's post-size limit instead of a 413.
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB across all files

// Labels are resolved at call time so they follow the active language; each
// `value` is the API keyword and must never be translated.
const getArgumentOptions = (t) => [
    { value: 'cancellation_withdrawn', label: t('submitDispute.argument.options.cancellationWithdrawn') },
    { value: 'refunded', label: t('submitDispute.argument.options.refunded') },
    { value: 'non_refundable', label: t('submitDispute.argument.options.nonRefundable') },
    { value: 'expired_claim', label: t('submitDispute.argument.options.expiredClaim') },
    { value: 'product_as_described', label: t('submitDispute.argument.options.productAsDescribed') },
    { value: 'credit_provided', label: t('submitDispute.argument.options.creditProvided') },
    { value: 'product_received', label: t('submitDispute.argument.options.productReceived') },
    { value: 'other', label: t('submitDispute.argument.options.other') },
];

const getDocumentTypeOptions = (t) => [
    { value: 'receipt', label: t('submitDispute.documents.types.receipt') },
    { value: 'invoice', label: t('submitDispute.documents.types.invoice') },
    { value: 'contract', label: t('submitDispute.documents.types.contract') },
    { value: 'proof_of_delivery', label: t('submitDispute.documents.types.proofOfDelivery') },
    { value: 'customer_communication', label: t('submitDispute.documents.types.customerCommunication') },
    { value: 'access_logs', label: t('submitDispute.documents.types.accessLogs') },
    { value: 'other', label: t('submitDispute.documents.types.other') },
];

function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(filename) {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return <PictureAsPdfRounded />;
    if (['jpg', 'jpeg', 'png'].includes(ext)) return <ImageRounded />;
    return <InsertDriveFileRounded />;
}

function toBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function DocumentRow({ doc, onRemove, onTypeChange }) {
    const { t } = useTranslation('contentModals');
    const documentTypeOptions = useMemo(() => getDocumentTypeOptions(t), [t]);

    return (
        <div className="dispute-modal__file-item">
            <span className="dispute-modal__file-icon">
                {getFileIcon(doc.name)}
            </span>

            <div className="dispute-modal__file-info">
                <Text.span className="dispute-modal__file-name">{doc.name}</Text.span>
                <Text.span contrast={6} className="dispute-modal__file-size">
                    {formatFileSize(doc.size)}
                </Text.span>
            </div>

            <div className="dispute-modal__file-type">
                <Input.Select
                    value={doc.type}
                    onInput={(val) => onTypeChange(doc.id, val)}
                    options={documentTypeOptions}
                    placeholder={t('submitDispute.documents.typePlaceholder')}
                    variant="light"
                    start={<Text.span>{t('common:fields.type')}</Text.span>}
                />
            </div>

            <button
                type="button"
                className="dispute-modal__file-remove"
                onClick={() => onRemove(doc.id)}
                title={t('submitDispute.documents.removeTitle')}
            >
                <CloseRounded style={{ fontSize: 16 }} />
            </button>
        </div>
    );
}

export default function SubmitDisputeModal({ modalRef }) {
    const { t } = useTranslation('contentModals');
    const argumentOptions = useMemo(() => getArgumentOptions(t), [t]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [argument, setArgument] = useState('');
    const [description, setDescription] = useState('');
    const [documents, setDocuments] = useState([]);
    const [errorTitle, setErrorTitle] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const REF_chargebackId = useRef(null);
    const REF_callback = useRef(null);
    const fileInputRef = useRef(null);

    modalRef.current = {
        open({ chargebackId }, callback) {
            REF_chargebackId.current = chargebackId;
            REF_callback.current = callback || null;
            setArgument('');
            setDescription('');
            setDocuments([]);
            setErrorTitle(null);
            setErrorMessage(null);
            setLoading(false);
            setOpen(true);
        },
        close() {
            setOpen(false);
        },
    };

    async function handleFileSelect(e) {
        const selected = Array.from(e.target.files || []);
        e.target.value = '';

        if (!selected.length) return;

        if (documents.length + selected.length > MAX_FILES) {
            setErrorTitle(t('submitDispute.errors.fileLimitTitle'));
            setErrorMessage(t('submitDispute.errors.fileLimitMessage', { count: MAX_FILES }));
            return;
        }

        const oversized = selected.find((f) => f.size > MAX_FILE_SIZE);
        if (oversized) {
            setErrorTitle(t('submitDispute.errors.fileTooLargeTitle'));
            setErrorMessage(t('submitDispute.errors.fileTooLargeMessage', { name: oversized.name }));
            return;
        }

        const currentTotal = documents.reduce((sum, d) => sum + (d.size || 0), 0);
        const selectedTotal = selected.reduce((sum, f) => sum + f.size, 0);
        if (currentTotal + selectedTotal > MAX_TOTAL_SIZE) {
            setErrorTitle(t('submitDispute.errors.totalLimitTitle'));
            setErrorMessage(t('submitDispute.errors.totalLimitMessage', { size: formatFileSize(MAX_TOTAL_SIZE) }));
            return;
        }

        setErrorTitle(null);
        setErrorMessage(null);

        const newDocs = await Promise.all(
            selected.map(async (file) => ({
                id: `${Date.now()}-${Math.random()}`,
                name: file.name,
                size: file.size,
                type: 'other',
                file_base64: await toBase64(file),
            }))
        );

        setDocuments((prev) => [...prev, ...newDocs]);
    }

    function handleRemoveDocument(id) {
        setDocuments((prev) => prev.filter((d) => d.id !== id));
    }

    function handleDocumentTypeChange(id, type) {
        setDocuments((prev) => prev.map((d) => (d.id === id ? { ...d, type } : d)));
    }

    async function handleSubmit() {
        if (!argument) {
            setErrorTitle(t('submitDispute.errors.argumentRequiredTitle'));
            setErrorMessage(t('submitDispute.errors.argumentRequiredMessage'));
            return;
        }

        if (!description.trim()) {
            setErrorTitle(t('submitDispute.errors.descriptionRequiredTitle'));
            setErrorMessage(t('submitDispute.errors.descriptionRequiredMessage'));
            return;
        }

        if (documents.length === 0) {
            setErrorTitle(t('submitDispute.errors.documentRequiredTitle'));
            setErrorMessage(t('submitDispute.errors.documentRequiredMessage'));
            return;
        }

        setLoading(true);
        setErrorTitle(null);
        setErrorMessage(null);

        try {
            const payload = {
                argument,
                description: description.trim(),
                documents: documents.map(({ type, file_base64 }) => ({ type, file_base64 })),
            };

            await api.post(`charge/chargeback/${REF_chargebackId.current}/dispute`, payload);

            window.setSnackbar(t('submitDispute.success'));

            if (REF_callback.current) await REF_callback.current();

            setOpen(false);
        } catch (e) {
            setErrorTitle(t('submitDispute.errors.submitTitle'));
            setErrorMessage(
                e?.response?.data?.message || t('submitDispute.errors.submitMessage')
            );
        }

        setLoading(false);
    }

    const canAddMore = documents.length < MAX_FILES;
    const descLength = description.length;

    return (
        <CustomModal
            maxWidth={580}
            title={t('submitDispute.modalTitle')}
            bottomButtonSumbitText={t('submitDispute.submitButton')}
            loading={loading}
            open={open}
            handleClose={() => !loading && setOpen(false)}
            handleSubmit={handleSubmit}
            errorTitle={errorTitle}
            errorMessage={errorMessage}
            modalClass="submit-dispute-modal-wrapper"
            bodyComponent={
                <div className="dispute-modal px-4 pt-3 pb-2">

                    {/* Argument */}
                    <div className="dispute-modal__section">
                        <Text.h4
                            weight={400}
                            left={<GavelRounded />}
                            className="dispute-modal__section-title"
                        >
                            {t('submitDispute.argument.title')}
                        </Text.h4>
                        <Text.span contrast={6} className="mb-2">
                            {t('submitDispute.argument.description')}
                        </Text.span>
                        <Input.Select
                            className="mt-2"
                            label={t('submitDispute.argument.label')}
                            value={argument}
                            onInput={setArgument}
                            options={argumentOptions}
                            placeholder={t('submitDispute.argument.placeholder')}
                        />
                    </div>

                    {/* Description */}
                    <div className="dispute-modal__section mt-4">
                        <Text.h4
                            weight={400}
                            left={<DescriptionRounded />}
                            className="dispute-modal__section-title"
                        >
                            {t('submitDispute.description.title')}
                        </Text.h4>
                        <Text.span contrast={6} className="mb-2">
                            {t('submitDispute.description.hint')}
                        </Text.span>
                        <Input.Text
                            className="mt-2"
                            label={t('submitDispute.description.label')}
                            value={description}
                            onInput={setDescription}
                            multiline
                            size={1000}
                        />
                    </div>

                    {/* Documents */}
                    <div className="dispute-modal__section mt-4">
                        <Text.h4
                            weight={400}
                            left={<AttachFileRounded />}
                            className="dispute-modal__section-title"
                        >
                            {t('submitDispute.documents.title')}
                        </Text.h4>
                        <Text.span contrast={6} className="mb-2">
                            {t('submitDispute.documents.hint', { count: MAX_FILES })}
                        </Text.span>

                        {documents.length > 0 && (
                            <div className="dispute-modal__file-list mt-2">
                                {documents.map((doc) => (
                                    <DocumentRow
                                        key={doc.id}
                                        doc={doc}
                                        onRemove={handleRemoveDocument}
                                        onTypeChange={handleDocumentTypeChange}
                                    />
                                ))}
                            </div>
                        )}

                        {canAddMore && (
                            <div className="dispute-modal__add-file mt-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.pdf"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />
                                <Button
                                    text={documents.length === 0 ? t('submitDispute.documents.addFirst') : t('submitDispute.documents.addMore')}
                                    color="white"
                                    size="small"
                                    startIcon={<AddRounded />}
                                    onClick={() => fileInputRef.current?.click()}

                                />
                                {documents.length > 0 && (
                                    <Text.span contrast={7}>
                                        {t('submitDispute.documents.counter', { current: documents.length, max: MAX_FILES })}
                                    </Text.span>
                                )}
                            </div>
                        )}
                    </div>

                </div>
            }
        />
    );
}
